/**
 * Real-Time Filter Availability Checker
 * Uses AJAX to check actual filter availability from Shopify based on current selections
 * Disables filters that would return 0 products in real-time
 */

class FilterAvailabilityChecker {
    constructor() {
        this.debounceTimeout = null;
        this.debounceDelay = 500; // ms - longer delay for AJAX requests
        this.currentCollection = this.getCurrentCollection();
        this.isChecking = false;
        this.init();
    }

    init() {
        const filterForm = document.querySelector('.js-filter-form');
        if (!filterForm) return;

        // Store initial filter states
        this.storeInitialFilterStates();

        // Add event listeners to all filter inputs
        const filterInputs = document.querySelectorAll('.js-filter-input[type="checkbox"]');
        filterInputs.forEach(input => {
            input.addEventListener('change', () => {
                this.debouncedCheckAvailability();
                this.updateAccordionState(input);
                this.updateButtonVisibility();
            });
        });

        // Initial check when page loads (no AJAX needed, use current page data)
        this.checkCurrentPageAvailability();
        this.initializeAccordionStates();
        this.setupAccordionClickHandlers();
        this.setupClearAllHandler();
        this.updateButtonVisibility();
    }

    getCurrentCollection() {
        const path = window.location.pathname;
        
        if (path.includes('/collections/')) {
            return path.split('/collections/')[1].split('/')[0];
        } else if (path === '/search') {
            return 'search';
        } else if (path === '/') {
            return 'all';
        }
        
        return 'all';
    }

    storeInitialFilterStates() {
        // Store which filters were initially disabled (count = 0)
        this.initiallyDisabled = new Set();
        
        const allInputs = document.querySelectorAll('.js-filter-input[type="checkbox"]');
        allInputs.forEach(input => {
            if (input.disabled) {
                this.initiallyDisabled.add(input.value);
            }
        });
    }

    checkCurrentPageAvailability() {
        // Use the current page's filter data (no AJAX needed on initial load)
        const filterGroups = document.querySelectorAll('.js-filter');
        filterGroups.forEach(group => {
            const inputs = group.querySelectorAll('.js-filter-input[type="checkbox"]');
            inputs.forEach(input => {
                if (input.disabled && !input.checked) {
                    const label = input.closest('label');
                    this.disableFilterOption(input, label);
                }
            });
        });
    }

    debouncedCheckAvailability() {
        if (this.isChecking) return; // Prevent multiple simultaneous requests
        
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(async () => {
            await this.checkFilterAvailabilityWithAjax();
        }, this.debounceDelay);
    }

    async checkFilterAvailabilityWithAjax() {
        if (this.isChecking) return;
        this.isChecking = true;

        try {
            const currentFilters = this.getCurrentlySelectedFilters();
            const testUrl = this.buildFilterTestUrl(currentFilters);
            
            // Show loading state
            this.showLoadingState();
            
            const response = await fetch(testUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'text/html',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const html = await response.text();
            this.parseFilterAvailabilityFromHTML(html);
            
        } catch (error) {
            console.warn('Filter availability check failed:', error);
            // Fallback: keep current state
        } finally {
            this.hideLoadingState();
            this.isChecking = false;
        }
    }

    buildFilterTestUrl(currentFilters) {
        let baseUrl = window.location.origin + window.location.pathname;
        
        // Handle search vs collection URLs
        if (this.currentCollection === 'search') {
            baseUrl = window.location.origin + '/search';
        } else if (this.currentCollection === 'all') {
            baseUrl = window.location.origin + '/collections/all';
        }

        const params = new URLSearchParams();
        
        // Add search query if it exists
        if (window.location.search) {
            const urlParams = new URLSearchParams(window.location.search);
            const query = urlParams.get('q');
            if (query) {
                params.set('q', query);
            }
        }

        // Add current filter selections
        Object.keys(currentFilters).forEach(paramName => {
            const values = currentFilters[paramName];
            if (values.length > 0) {
                params.set(paramName, values.join(','));
            }
        });

        return baseUrl + (params.toString() ? '?' + params.toString() : '');
    }

    parseFilterAvailabilityFromHTML(html) {
        // Create a temporary DOM parser
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Find all filter inputs in the response
        const responseFilters = doc.querySelectorAll('.js-filter-input[type="checkbox"]');
        
        responseFilters.forEach(responseInput => {
            const value = responseInput.value;
            const isDisabled = responseInput.disabled;
            
            // Find the corresponding input on our current page
            const currentInput = document.querySelector(`.js-filter-input[value="${CSS.escape(value)}"]`);
            if (currentInput) {
                const label = currentInput.closest('label');
                const isCurrentlyChecked = currentInput.checked;
                
                // Don't disable currently selected filters
                if (isCurrentlyChecked) {
                    this.enableFilterOption(currentInput, label);
                } else if (isDisabled) {
                    this.disableFilterOption(currentInput, label);
                } else {
                    this.enableFilterOption(currentInput, label);
                }
            }
        });
    }

    getCurrentlySelectedFilters() {
        const filters = {};
        const filterGroups = document.querySelectorAll('.js-filter');
        
        filterGroups.forEach(group => {
            const paramName = group.dataset.param_name;
            const checkedInputs = group.querySelectorAll('.js-filter-input[type="checkbox"]:checked');
            
            if (checkedInputs.length > 0) {
                const values = Array.from(checkedInputs).map(input => input.value);
                filters[paramName] = values;
            }
        });

        return filters;
    }

    showLoadingState() {
        // Add a subtle loading indicator
        const filterForm = document.querySelector('.js-filter-form');
        if (filterForm) {
            filterForm.classList.add('checking-availability');
        }
    }

    hideLoadingState() {
        // Remove loading indicator
        const filterForm = document.querySelector('.js-filter-form');
        if (filterForm) {
            filterForm.classList.remove('checking-availability');
        }
    }


    initializeAccordionStates() {
        // Set initial accordion states based on active filters
        const filterGroups = document.querySelectorAll('.js-filter');
        filterGroups.forEach(group => {
            this.updateAccordionStateForGroup(group);
        });
    }

    updateAccordionState(input) {
        // Update accordion state when a filter input changes
        const filterGroup = input.closest('.js-filter');
        if (filterGroup) {
            this.updateAccordionStateForGroup(filterGroup);
        }
    }

    updateAccordionStateForGroup(filterGroup) {
        const filterTrigger = filterGroup.querySelector('.js-filter-trigger');
        const filterGroupContainer = filterGroup.querySelector('.js-filter-group');
        
        if (!filterTrigger || !filterGroupContainer) return;

        // Check if this group has any active (checked) filters
        const activeFilters = filterGroup.querySelectorAll('.js-filter-input[type="checkbox"]:checked');
        const hasActiveFilters = activeFilters.length > 0;

        // If there are active filters, keep the accordion open
        if (hasActiveFilters) {
            filterTrigger.classList.add('active');
            filterGroupContainer.classList.add('active');
        } else {
            // If no active filters, only close if user manually closed it
            // Check if user has manually interacted with this accordion
            if (!this.isAccordionManuallyManaged(filterGroup)) {
                filterTrigger.classList.remove('active');
                filterGroupContainer.classList.remove('active');
            }
        }
    }

    isAccordionManuallyManaged(filterGroup) {
        // Check if user has manually clicked on this accordion
        // We'll track this by adding a data attribute when the trigger is clicked
        return filterGroup.hasAttribute('data-manually-managed');
    }

    setupAccordionClickHandlers() {
        // Add additional logic to existing accordion click handlers
        const filterTriggers = document.querySelectorAll('.js-filter-trigger');
        filterTriggers.forEach(filterTrigger => {
            // Add our tracking logic to existing click handlers
            filterTrigger.addEventListener('click', (e) => {
                const filterGroup = filterTrigger.closest('.js-filter');
                if (filterGroup) {
                    // Mark this accordion as manually managed
                    filterGroup.setAttribute('data-manually-managed', 'true');
                }
            });
        });
    }

    setupClearAllHandler() {
        // Handle "Clear All" button to reset accordion states
        const clearAllBtn = document.querySelector('.js-clear-filters');
        if (clearAllBtn) {
            clearAllBtn.addEventListener('click', () => {
                // Reset all accordion manual management flags
                const filterGroups = document.querySelectorAll('.js-filter');
                filterGroups.forEach(group => {
                    group.removeAttribute('data-manually-managed');
                });
                
                // Update button visibility after clearing
                setTimeout(() => {
                    this.updateButtonVisibility();
                }, 100);
            });
        }
    }

    updateButtonVisibility() {
        // Show/hide Apply and Clear buttons based on filter selection
        const applyBtn = document.querySelector('.js-apply-filters');
        const clearBtn = document.querySelector('.js-clear-filters');
        const buttonContainer = document.querySelector('.filter-apply-container');
        
        if (!buttonContainer) return;

        // Check if any filters are selected
        const selectedFilters = document.querySelectorAll('.js-filter-input[type="checkbox"]:checked');
        const hasSelectedFilters = selectedFilters.length > 0;

        if (hasSelectedFilters) {
            buttonContainer.style.display = 'flex';
            if (applyBtn) applyBtn.style.display = 'inline-block';
            if (clearBtn) clearBtn.style.display = 'inline-block';
        } else {
            buttonContainer.style.display = 'none';
            if (applyBtn) applyBtn.style.display = 'none';
            if (clearBtn) clearBtn.style.display = 'none';
        }
    }

    disableFilterOption(input, label) {
        input.disabled = true;
        label.classList.add('filter-disabled');
        label.setAttribute('title', 'This filter may not return any results with your current selection');
    }

    enableFilterOption(input, label) {
        input.disabled = false;
        label.classList.remove('filter-disabled');
        label.removeAttribute('title');
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new FilterAvailabilityChecker();
    });
} else {
    new FilterAvailabilityChecker();
}
